// Intent: the "3 ikonki" alternative bodies of the summary shade (owner:
// "można wyświetlić jako to koło, listę albo takie tekstowe podsumowanie").
// The chart body lives in index.tsx (it reuses BudgetPaceChart); these are the
// LIST (that day's expense entries) and TEXT (plain-Polish paragraph) bodies.
// All copy comes from interpolated i18n keys — never concatenated in JSX.

import { useTranslation } from 'react-i18next';
import { View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';

import { CATEGORY_ICONS } from '@/components/category-grid';
import { AppText } from '@/components/ui/app-text';
import { colors, spacing } from '@/theme/tokens';
import type { BudgetEntry } from '@/types/domain';
import { formatPln } from '@/utils/format';

export function DayEntriesList({ dayEntries }: { dayEntries: BudgetEntry[] }) {
  const { t } = useTranslation();
  if (dayEntries.length === 0) {
    return (
      <AppText variant="bodyMd" color={colors.textSecondary}>
        {t('forecastHistory.listEmpty')}
      </AppText>
    );
  }
  return (
    <View>
      {dayEntries.map((e, idx) => (
        <View
          key={e.id}
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            gap: spacing.sm,
            paddingVertical: spacing.xs,
            borderTopWidth: idx === 0 ? 0 : 1,
            borderTopColor: colors.divider,
          }}
        >
          <Ionicons name={CATEGORY_ICONS[e.categoryId] ?? 'pricetag-outline'} size={18} color={colors.navy} />
          <AppText variant="bodyLg" style={{ flex: 1 }} numberOfLines={1}>
            {e.note?.trim() ? e.note : t(`budget.categories.${e.categoryId}`)}
          </AppText>
          <AppText variant="labelLg" color={colors.navy}>
            {formatPln(e.amountGr)}
          </AppText>
        </View>
      ))}
    </View>
  );
}

export function DayTextSummary({
  dateLabel,
  daySpentGr,
  monthSpentGr,
  forecastLeftGr,
}: {
  dateLabel: string;
  daySpentGr: number;
  monthSpentGr: number;
  /** Recorded forecast of that day (snapshot) — omitted when none exists. */
  forecastLeftGr: number | null;
}) {
  const { t } = useTranslation();
  return (
    <View style={{ gap: spacing.xs }}>
      <AppText variant="bodyLg">
        {t('forecastHistory.textBody', {
          date: dateLabel,
          daySpent: formatPln(daySpentGr),
          monthSpent: formatPln(monthSpentGr),
        })}
      </AppText>
      {forecastLeftGr !== null && (
        <AppText variant="bodyMd" color={colors.textSecondary}>
          {forecastLeftGr >= 0
            ? t('forecastHistory.textLeftPositive', { amount: formatPln(forecastLeftGr) })
            : t('forecastHistory.textLeftNegative', { amount: formatPln(-forecastLeftGr) })}
        </AppText>
      )}
    </View>
  );
}
